/**
 * Time clock (change request 2026-11): clock-out choices, notes at every clock-out, non-billable
 * shop time and office timekeeping. Time that isn't on a work order is a `time_clock_punches`
 * row in a category from settings (`timeClock`), never a fake work order, so the work order
 * number sequence is untouched.
 */
import { z } from 'zod';
/** Least characters for the Work Performed note that goes with every clock-out. */
export const WORK_NOTE_MIN = 3;
/** Why a punch ended, as chosen on the iPad / office clock. SWITCH = clocked into something else. */
export const CLOCK_OUT_REASONS = ['SWITCH', 'LUNCH', 'DAY', 'OUT'];
export const ClockOutReason = z.enum(CLOCK_OUT_REASONS);
export const CLOCK_OUT_LABELS = {
    SWITCH: 'Switched',
    LUNCH: 'Out for Lunch',
    DAY: 'Out for the Day',
    OUT: 'Clocked out',
};
export const TIME_KINDS = ['NON_BILLABLE', 'OFFICE'];
export const TimeCategory = z.object({
    key: z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
    label: z.string().trim().min(1).max(80),
    /** Timesheet column: non-billable shop time, or office (payroll) time. */
    kind: z.enum(TIME_KINDS),
    /** Who may clock into it: mechanics (needs the time.shop capability) or office users who track hours. */
    base: z.enum(['OFFICE', 'MECHANIC']),
    /** A note saying what was done is required at clock-out. */
    noteRequired: z.boolean(),
});
export const TimeClockSettings = z
    .object({ categories: z.array(TimeCategory).min(1) })
    .refine((s) => new Set(s.categories.map((c) => c.key)).size === s.categories.length, 'timeClock category keys must be unique');
export const DEFAULT_TIME_CLOCK = {
    categories: [
        { key: 'SHOP', label: 'Shop / Non-billable', kind: 'NON_BILLABLE', base: 'MECHANIC', noteRequired: true },
        { key: 'OFFICE', label: 'Office time', kind: 'OFFICE', base: 'OFFICE', noteRequired: false },
    ],
};
/** Office clock (Zain): POST /api/time-clock/in and /out. */
export const OfficeClockIn = z.object({ category: z.string().max(40).optional() });
export const OfficeClockOut = z.object({
    reason: z.enum(['LUNCH', 'DAY']),
    note: z.string().trim().max(10_000).optional(),
});
//# sourceMappingURL=time-clock.js.map